import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Users, Shield, TrendingUp } from "lucide-react";
import { Reveal, Stagger, staggerItem } from "@/components/Motion";
import { motion } from "framer-motion";
import CtaBanner from "@/components/CtaBanner";
import Seo from "@/components/Seo";
import { IMAGES } from "@/lib/site-data";

export default function Employers() {
  const solutions = [
    { Icon: Briefcase, title: "Direct hire", body: "Permanent placements for senior engineering, architecture, and leadership roles." },
    { Icon: Users, title: "Contract & C2H", body: "Flexible contract and contract-to-hire engineers ready in 7–14 days." },
    { Icon: Shield, title: "Team augmentation", body: "Drop-in pods — engineers, DevOps, data, security — fully managed by Cutting Edge Technologies." },
    { Icon: TrendingUp, title: "Upskill-to-hire", body: "We train and place emerging talent into your roles on your specs." },
  ];

  return (
    <div data-testid="page-employers">
      <Seo
        title="For Employers | Hire Vetted Tech Talent — Houston TX, Washington DC"
        description="Pre-vetted engineers, DevOps, cloud, data, and AI/ML talent for contract, contract-to-hire, and direct placement. Serving Houston, Richmond, Sugar Land, Fulshear TX and Washington DC."
        path="/employers"
      />
      <section className="relative bg-slate-950 text-white pt-36 pb-24 md:pt-44 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.employers} alt="Professionals collaborating" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-slate-950/40" />
          <div className="absolute inset-0 grid-lines-dark opacity-50" />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Reveal><div className="eyebrow text-emerald-400">For Employers</div></Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02]">
                Hire tech talent that's ready on day one.
              </h1>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-8 max-w-2xl text-lg md:text-xl text-slate-300 leading-relaxed">
                Access pre-vetted, trained engineers and architects — matched to your roadmap, not
                your job description. Contract, contract-to-hire, and direct placements across cloud,
                DevOps, data, security, AI/ML, and modern application engineering.
              </p>
            </Reveal>
            <Reveal delay={0.28}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/contact" data-testid="employers-hire-cta" className="group inline-flex items-center gap-2 bg-emerald-500 text-slate-950 px-8 py-4 font-medium hover:bg-emerald-400 transition-colors">
                  Hire Talent <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.75} />
                </Link>
                <Link to="/services#staffing" className="inline-flex items-center gap-2 border border-slate-700 text-white px-8 py-4 font-medium hover:border-emerald-500 hover:text-emerald-400 transition-colors">
                  How we staff
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <div className="relative">
                <div className="absolute inset-0 border border-emerald-500 translate-x-3 translate-y-3" />
                <div className="relative bg-slate-900/80 backdrop-blur border border-slate-800 p-8">
                  <div className="eyebrow text-emerald-400">Why employers partner with us</div>
                  <ul className="mt-5 space-y-4 text-slate-300">
                    <li className="flex items-start gap-3"><span className="h-2 w-2 bg-emerald-500 mt-2" /> <span>Senior-only bench: 7+ yrs avg tenure</span></li>
                    <li className="flex items-start gap-3"><span className="h-2 w-2 bg-emerald-500 mt-2" /> <span>14-day typical ramp, US + nearshore</span></li>
                    <li className="flex items-start gap-3"><span className="h-2 w-2 bg-emerald-500 mt-2" /> <span>Diversity-forward sourcing pipeline</span></li>
                    <li className="flex items-start gap-3"><span className="h-2 w-2 bg-emerald-500 mt-2" /> <span>Transparent pricing and replacement guarantee</span></li>
                  </ul>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-28 md:py-36 bg-slate-50">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 items-end">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="eyebrow text-emerald-600">Hiring solutions</div>
                <h2 className="mt-4 font-display text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                  Flexible models for every stage of growth.
                </h2>
              </Reveal>
            </div>
          </div>
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {solutions.map((s, i) => (
              <motion.div key={s.title} variants={staggerItem} className="bg-white border border-slate-200 p-8 hover:border-slate-900 hover:-translate-y-1 transition-all" data-testid={`employer-solution-${i}`}>
                <s.Icon className="w-6 h-6 text-emerald-600" strokeWidth={1.5} />
                <div className="eyebrow mt-6 text-slate-500">0{i + 1}</div>
                <h3 className="mt-2 font-display text-xl font-semibold text-slate-900 tracking-tight">{s.title}</h3>
                <p className="mt-3 text-slate-600 text-sm leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* How it works */}
      <section className="py-28 md:py-36 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Reveal>
            <div className="eyebrow text-emerald-600">How it works</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
              From intake to onboarded — in two weeks.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-5">
            {[
              { n: "01", t: "Intake call", d: "30-minute discovery — role, tech stack, team culture, timeline." },
              { n: "02", t: "Curated shortlist", d: "3–5 pre-vetted candidates delivered within 72 hours." },
              { n: "03", t: "Interview loop", d: "We coordinate panels, feedback, and negotiation." },
              { n: "04", t: "Onboarded", d: "Day-one ready — equipment, access, and 30/60/90 plan." },
            ].map((step, i) => (
              <Reveal key={step.n} delay={i * 0.08}>
                <div className="border-l-2 border-emerald-500 pl-6">
                  <div className="font-mono text-xs tracking-widest text-emerald-600">{step.n}</div>
                  <div className="mt-3 font-display text-xl font-semibold text-slate-900">{step.t}</div>
                  <p className="mt-2 text-slate-600 text-sm leading-relaxed">{step.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner
        eyebrow="Hire Talent"
        title="Tell us who you need. We'll bring the shortlist."
        body="Most roles are scoped, sourced, and placed within 14 days of our first call."
        primary={{ to: "/contact", label: "Hire Talent" }}
      />
    </div>
  );
}
